import { createContext, useContext, useState, type ReactNode } from "react";

import {
  stagedContent,
  type ConversationTurnInput,
} from "../components/chat/conversation";

const PROJECT_TURN_OUTBOX_SCHEMA = "sx.project-turn-outbox/v1" as const;

interface ProjectTurnOutboxRecord {
  readonly schema: typeof PROJECT_TURN_OUTBOX_SCHEMA;
  readonly outboxId: string;
  readonly principalId: string;
  readonly projectId: string;
  readonly conversationId: string | null;
  readonly turnSignature: string;
  readonly idempotencyKey: string;
  readonly createdAt: string;
}

export interface ProjectTurnOutboxStore {
  /** Atomically returns the retained record or inserts this first attempt. */
  readonly acquire: (
    candidate: ProjectTurnOutboxRecord,
  ) => Promise<ProjectTurnOutboxRecord>;
  /** Deletes only the exact admitted attempt. */
  readonly remove: (record: ProjectTurnOutboxRecord) => Promise<void>;
}

const DATABASE = "sx-project-turn-outbox";
const VERSION = 1;
const STORE = "turns";
const MAX_SIGNATURE_BYTES = 4 * 1024 * 1024;
const MAX_IDENTITY_LENGTH = 512;
const SHA256 = /^[0-9a-f]{64}$/;
const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

class ProjectTurnOutboxPersistenceError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ProjectTurnOutboxPersistenceError";
  }
}

export class ProjectTurnOutbox {
  constructor(
    readonly principalId: string,
    private readonly store: ProjectTurnOutboxStore,
  ) {
    requireIdentity(principalId, "principal");
  }

  async admit<Result>(
    projectId: string,
    conversationId: string | null,
    turn: ConversationTurnInput,
    owner: (idempotencyKey: string) => Promise<Result>,
    intent?: string,
  ): Promise<Result> {
    requireIdentity(projectId, "project");
    if (conversationId !== null)
      requireIdentity(conversationId, "conversation");
    const turnSignature =
      intent === undefined
        ? projectTurnSignature(turn)
        : JSON.stringify({ turn: projectTurnSignature(turn), intent });
    if (
      new TextEncoder().encode(turnSignature).byteLength > MAX_SIGNATURE_BYTES
    )
      throw new TypeError(
        "The complete request is too large to retain safely.",
      );
    const outboxId = await projectTurnOutboxId(
      this.principalId,
      projectId,
      conversationId,
      turnSignature,
    );
    const candidate: ProjectTurnOutboxRecord = {
      schema: PROJECT_TURN_OUTBOX_SCHEMA,
      outboxId,
      principalId: this.principalId,
      projectId,
      conversationId,
      turnSignature,
      idempotencyKey: randomIdempotencyKey(),
      createdAt: new Date().toISOString(),
    };
    const retained = validateRecord(await this.store.acquire(candidate));
    assertSameTurn(candidate, retained);
    const result = await owner(retained.idempotencyKey);
    await this.store.remove(retained);
    return result;
  }
}

/** One canonical signature for a Project turn, independent of object property order. */
export function projectTurnSignature(turn: ConversationTurnInput): string {
  const signature = JSON.stringify({
    content: turn.content,
    // The retained signature keeps its own snake_case JSON, so a record written before
    // the attachment became the generated message still names the same turn.
    attachments: turn.attachments.map((attachment) => {
      const content = stagedContent(attachment);
      return {
        content: { artifact_id: content.artifactId, sha256: content.sha256 },
        filename: attachment.filename,
        media_type: attachment.mediaType,
        byte_size: Number(attachment.byteSize),
      };
    }),
    // A turn no longer carries a preset or skills; the signature keeps their old values so
    // a turn retained before they were retired still names the same turn.
    skills: [],
    mode: "project",
    ...(turn.environmentSource === undefined
      ? {}
      : {
          environmentSource: {
            pillar: turn.environmentSource.pillar,
            kind: turn.environmentSource.kind,
            operation_id: turn.environmentSource.operation_id,
          },
        }),
  });
  if (new TextEncoder().encode(signature).byteLength > MAX_SIGNATURE_BYTES) {
    throw new TypeError("The Project turn is too large to retain safely.");
  }
  return signature;
}

function indexedDbProjectTurnOutboxStore(
  factory: IDBFactory | undefined = globalThis.indexedDB,
): ProjectTurnOutboxStore {
  if (factory === undefined) {
    throw new ProjectTurnOutboxPersistenceError(
      "This browser cannot retain a Project turn across retry.",
    );
  }
  let opened: Promise<IDBDatabase> | undefined;
  const database = () => (opened ??= openDatabase(factory));
  return {
    acquire: async (candidate) =>
      acquireRecord(await database(), validateRecord(candidate)),
    remove: async (record) =>
      removeRecord(await database(), validateRecord(record)),
  };
}

/** Deterministic test/embedded store with the same exact-attempt fencing contract. */
export function memoryProjectTurnOutboxStore(): ProjectTurnOutboxStore {
  const records = new Map<string, ProjectTurnOutboxRecord>();
  return {
    acquire: async (candidate) => {
      const expected = validateRecord(candidate);
      const existing = records.get(expected.outboxId);
      if (existing !== undefined) {
        const retained = validateRecord(existing);
        assertSameTurn(expected, retained);
        return retained;
      }
      records.set(expected.outboxId, expected);
      return expected;
    },
    remove: async (record) => {
      const expected = validateRecord(record);
      const current = records.get(expected.outboxId);
      if (current === undefined) return;
      assertSameAttempt(expected, validateRecord(current));
      records.delete(expected.outboxId);
    },
  };
}

function acquireRecord(
  database: IDBDatabase,
  candidate: ProjectTurnOutboxRecord,
): Promise<ProjectTurnOutboxRecord> {
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE, "readwrite");
    const store = transaction.objectStore(STORE);
    const request = store.get(candidate.outboxId);
    let retained: ProjectTurnOutboxRecord | undefined;
    let refusal: unknown;
    request.addEventListener("success", () => {
      try {
        if (request.result === undefined) {
          retained = candidate;
          store.add(candidate);
        } else {
          retained = validateRecord(request.result);
          assertSameTurn(candidate, retained);
        }
      } catch (cause) {
        refusal = cause;
        transaction.abort();
      }
    });
    transaction.addEventListener("complete", () => {
      if (retained === undefined) {
        reject(
          new ProjectTurnOutboxPersistenceError(
            "The retained Project turn is incomplete.",
          ),
        );
        return;
      }
      resolve(retained);
    });
    transaction.addEventListener("abort", () =>
      reject(refusal ?? persistenceFailure(transaction.error)),
    );
    transaction.addEventListener("error", () =>
      reject(refusal ?? persistenceFailure(transaction.error)),
    );
  });
}

function removeRecord(
  database: IDBDatabase,
  record: ProjectTurnOutboxRecord,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE, "readwrite");
    const store = transaction.objectStore(STORE);
    const request = store.get(record.outboxId);
    let refusal: unknown;
    request.addEventListener("success", () => {
      try {
        if (request.result === undefined) return;
        assertSameAttempt(record, validateRecord(request.result));
        store.delete(record.outboxId);
      } catch (cause) {
        refusal = cause;
        transaction.abort();
      }
    });
    transaction.addEventListener("complete", () => resolve());
    transaction.addEventListener("abort", () =>
      reject(refusal ?? persistenceFailure(transaction.error)),
    );
    transaction.addEventListener("error", () =>
      reject(refusal ?? persistenceFailure(transaction.error)),
    );
  });
}

function openDatabase(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let request: IDBOpenDBRequest;
    try {
      request = factory.open(DATABASE, VERSION);
    } catch (cause) {
      reject(persistenceFailure(cause));
      return;
    }
    request.addEventListener("upgradeneeded", () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE)) {
        database.createObjectStore(STORE, { keyPath: "outboxId" });
      }
    });
    request.addEventListener("success", () => resolve(request.result));
    request.addEventListener("error", () =>
      reject(persistenceFailure(request.error)),
    );
    request.addEventListener("blocked", () =>
      reject(
        new ProjectTurnOutboxPersistenceError(
          "Project turn storage is blocked by another Console tab.",
        ),
      ),
    );
  });
}

function validateRecord(value: unknown): ProjectTurnOutboxRecord {
  const record = value as Partial<ProjectTurnOutboxRecord>;
  if (
    record.schema !== PROJECT_TURN_OUTBOX_SCHEMA ||
    !SHA256.test(record.outboxId ?? "") ||
    !validIdentity(record.principalId) ||
    !validIdentity(record.projectId) ||
    (record.conversationId !== null && !validIdentity(record.conversationId)) ||
    !record.turnSignature ||
    new TextEncoder().encode(record.turnSignature).byteLength >
      MAX_SIGNATURE_BYTES ||
    !UUID.test(record.idempotencyKey ?? "") ||
    typeof record.createdAt !== "string" ||
    !Number.isFinite(Date.parse(record.createdAt))
  ) {
    throw new ProjectTurnOutboxPersistenceError(
      "The retained Project turn is corrupt.",
    );
  }
  return record as ProjectTurnOutboxRecord;
}

function assertSameTurn(
  expected: ProjectTurnOutboxRecord,
  retained: ProjectTurnOutboxRecord,
): void {
  if (
    expected.outboxId !== retained.outboxId ||
    expected.principalId !== retained.principalId ||
    expected.projectId !== retained.projectId ||
    expected.conversationId !== retained.conversationId ||
    expected.turnSignature !== retained.turnSignature
  ) {
    throw new ProjectTurnOutboxPersistenceError(
      "The retained Project turn belongs to another request.",
    );
  }
}

function assertSameAttempt(
  expected: ProjectTurnOutboxRecord,
  retained: ProjectTurnOutboxRecord,
): void {
  assertSameTurn(expected, retained);
  if (expected.idempotencyKey !== retained.idempotencyKey) {
    throw new ProjectTurnOutboxPersistenceError(
      "The retained Project turn attempt changed before cleanup.",
    );
  }
}

async function projectTurnOutboxId(
  principalId: string,
  projectId: string,
  conversationId: string | null,
  turnSignature: string,
): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    throw new ProjectTurnOutboxPersistenceError(
      "This browser cannot safely identify a retained Project turn.",
    );
  }
  const bytes = new TextEncoder().encode(
    JSON.stringify([principalId, projectId, conversationId, turnSignature]),
  );
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
}

function randomIdempotencyKey(): string {
  const key = globalThis.crypto?.randomUUID?.();
  if (!key) {
    throw new ProjectTurnOutboxPersistenceError(
      "This browser cannot safely identify a Project turn attempt.",
    );
  }
  return key;
}

function requireIdentity(value: string, name: string): void {
  if (!validIdentity(value))
    throw new TypeError(`A Project turn requires a ${name} identity.`);
}

function validIdentity(value: unknown): value is string {
  return (
    typeof value === "string" &&
    Boolean(value.trim()) &&
    value.length <= MAX_IDENTITY_LENGTH
  );
}

function persistenceFailure(cause: unknown): ProjectTurnOutboxPersistenceError {
  if (cause instanceof ProjectTurnOutboxPersistenceError) return cause;
  if (cause instanceof DOMException && cause.name === "QuotaExceededError") {
    return new ProjectTurnOutboxPersistenceError(
      "This browser has no space left to retain the Project turn.",
    );
  }
  return new ProjectTurnOutboxPersistenceError(
    cause instanceof Error
      ? `The Project turn could not be retained: ${cause.message}`
      : "The Project turn could not be retained.",
  );
}

const ProjectTurnOutboxContext = createContext<ProjectTurnOutbox | null>(null);

export function ProjectTurnOutboxProvider({
  principalId,
  store,
  children,
}: {
  readonly principalId: string;
  readonly store?: ProjectTurnOutboxStore;
  readonly children: ReactNode;
}) {
  const [outbox] = useState(() => {
    try {
      return new ProjectTurnOutbox(
        principalId,
        store ?? indexedDbProjectTurnOutboxStore(),
      );
    } catch {
      return null;
    }
  });
  return (
    <ProjectTurnOutboxContext.Provider value={outbox}>
      {children}
    </ProjectTurnOutboxContext.Provider>
  );
}

export function useProjectTurnOutbox(): ProjectTurnOutbox | null {
  return useContext(ProjectTurnOutboxContext);
}
